import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { JobCard } from "@/components/JobCard";
import { AdCard } from "@/components/AdCard";
import { CatIcon, CITIES } from "@/lib/categories";
import { useAutoCity } from "@/hooks/useAutoCity";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, SlidersHorizontal, Loader2 } from "lucide-react";

const LIMIT = 10;

export default function Jobs() {
  const [params] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const [city, setCity] = useState(params.get("city") || "");
  const [category, setCategory] = useState(params.get("category") || "all");
  const [maxBudget, setMaxBudget] = useState(100000);
  const [material, setMaterial] = useState("");
  const [sinceDays, setSinceDays] = useState("");
  const [sort, setSort] = useState("newest");
  const detectedCity = useAutoCity();
  const sentinel = useRef(null);

  useEffect(() => { if (detectedCity && !params.get("city") && !city) setCity(detectedCity); }, [detectedCity]);
  useEffect(() => { api.get("/categories").then(({ data }) => setCategories(data)); }, []);

  const fetchPage = useCallback(async (pg) => {
    setLoading(true);
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (city) p.set("city", city);
    if (category && category !== "all") p.set("category", category);
    if (maxBudget < 100000) p.set("budget_max", maxBudget);
    if (material) p.set("material_status", material);
    if (sinceDays) p.set("since_days", sinceDays);
    p.set("sort", sort); p.set("page", pg); p.set("limit", LIMIT);
    const { data } = await api.get(`/jobs?${p.toString()}`);
    setItems((prev) => (pg === 1 ? data.items : [...prev, ...data.items]));
    setPages(data.pages); setTotal(data.total); setPage(pg);
    setLoading(false);
  }, [q, city, category, maxBudget, material, sinceDays, sort]);

  useEffect(() => { setItems([]); fetchPage(1); }, [fetchPage]);

  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !loading && page < pages) fetchPage(page + 1);
    }, { rootMargin: "400px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [loading, page, pages, fetchPage]);

  const rendered = [];
  items.forEach((j, i) => {
    rendered.push(<JobCard key={j.id} job={j} />);
    if ((i + 1) % 6 === 0) rendered.push(<AdCard key={`ad-${i}`} />);
  });

  return (
    <div>
      <div className="tinted border-b border-emerald-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900">İş İlanları</h1>
          <p className="text-slate-600 mt-2">Size uygun işleri keşfedin, yetenekli ustalarla buluşun.</p>
          <div className="bg-white rounded-2xl shadow-md border border-emerald-100 p-2 mt-5 flex flex-col sm:flex-row gap-2 max-w-3xl">
            <div className="flex-1 flex items-center gap-2 px-3">
              <Search className="h-5 w-5 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="İş başlığı, anahtar kelime ile arayın..." className="border-0 focus-visible:ring-0 shadow-none px-0" data-testid="jobs-search-input" />
            </div>
            <Select value={city || "all"} onValueChange={(v) => setCity(v === "all" ? "" : v)}>
              <SelectTrigger className="sm:w-52 rounded-xl" data-testid="jobs-city-filter"><SelectValue placeholder="Tüm Türkiye" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Türkiye</SelectItem>
                {CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid lg:grid-cols-[280px_1fr] gap-8">
        <aside className="bg-white rounded-2xl border border-slate-200/80 p-5 h-fit space-y-6">
          <div>
            <div className="flex items-center gap-2 font-bold text-slate-900 mb-3"><SlidersHorizontal className="h-4 w-4 text-emerald-500" /> Kategoriler</div>
            <div className="space-y-1">
              <button onClick={() => setCategory("all")} className={`w-full text-left text-sm px-3 py-2 rounded-lg flex items-center gap-2 ${category === "all" ? "bg-emerald-50 text-emerald-700 font-semibold" : "text-slate-600 hover:bg-slate-50"}`}>Tümü</button>
              {categories.map((c) => (
                <button key={c.slug} data-testid={`jobs-cat-${c.slug}`} onClick={() => setCategory(c.slug)}
                  className={`w-full text-left text-sm px-3 py-2 rounded-lg flex items-center gap-2 ${category === c.slug ? "bg-emerald-50 text-emerald-700 font-semibold" : "text-slate-600 hover:bg-slate-50"}`}>
                  <CatIcon name={c.icon} className="h-4 w-4" /> {c.name}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="font-bold text-slate-900 mb-3">Fiyat Aralığı</div>
            <Slider value={[maxBudget]} onValueChange={(v) => setMaxBudget(v[0])} max={100000} step={5000} className="mt-2" data-testid="jobs-budget-slider" />
            <div className="flex justify-between text-xs text-slate-500 mt-2">
              <span>0 TL</span><span>{maxBudget >= 100000 ? "100.000+ TL" : `${new Intl.NumberFormat("tr-TR").format(maxBudget)} TL`}</span>
            </div>
          </div>
          <div>
            <div className="font-bold text-slate-900 mb-3">Malzeme Durumu</div>
            <Select value={material || "all"} onValueChange={(v) => setMaterial(v === "all" ? "" : v)}>
              <SelectTrigger className="rounded-xl" data-testid="jobs-material-filter"><SelectValue placeholder="Tümü" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="dahil">Malzeme dahil</SelectItem>
                <SelectItem value="dahil değil">Malzeme dahil değil</SelectItem>
                <SelectItem value="görüşülecek">Görüşülecek</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="font-bold text-slate-900 mb-3">İlan Tarihi</div>
            <Select value={sinceDays || "all"} onValueChange={(v) => setSinceDays(v === "all" ? "" : v)}>
              <SelectTrigger className="rounded-xl" data-testid="jobs-date-filter"><SelectValue placeholder="Tümü" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="1">Son 24 saat</SelectItem>
                <SelectItem value="3">Son 3 gün</SelectItem>
                <SelectItem value="7">Son 7 gün</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </aside>

        <div>
          <div className="flex items-center justify-between mb-5">
            <p className="text-sm text-slate-600"><span className="font-bold text-emerald-600">{total}</span> iş ilanı bulundu</p>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-48 rounded-xl bg-white" data-testid="jobs-sort-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">En Yeni İlanlar</SelectItem>
                <SelectItem value="oldest">En Eski İlanlar</SelectItem>
                <SelectItem value="budget_asc">Bütçe: Düşükten Yükseğe</SelectItem>
                <SelectItem value="budget_desc">Bütçe: Yüksekten Düşüğe</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {!loading && items.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200/80 p-12 text-center text-slate-500">Bu kriterlere uygun ilan bulunamadı.</div>
          ) : (
            <div className="space-y-4">{rendered}</div>
          )}
          <div ref={sentinel} className="h-10" />
          {loading && <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-emerald-500" /></div>}
        </div>
      </div>
    </div>
  );
}
